import type { SavedView, ViewFilters } from "../../domain/app-types.ts";
import { randomUuid } from "../../infrastructure/crypto/random.ts";
import { changeEpic, changeCategory, changeTodo, createCategory, createCycle, createSpaceTemplateBundle, createTemplate } from "../../infrastructure/sync/automerge.ts";
import { boardCardFields } from "../tasks/board-configuration.ts";
import { normalizeSortDirection, normalizeTaskSort } from "../tasks/selectors.ts";
import type { PortableSpace } from "./transfer.ts";
import { dependencyOrder } from "./import-order.ts";
import { parsePortableSpaceTemplate, portableSpaceTemplate } from "../navigation/space-template-bundle.ts";
import { hierarchyParentId } from "../tasks/task-hierarchy.ts";
import { remapMarkdownLinks } from "./markdown-portability.ts";
import { parseReferenceHistory } from "../notes/reference-history.ts";
import type { ProgressReporter } from "../../ui/operation-progress.ts";
import { parseContentHistory } from "../notes/content-history.ts";
import { isCrossSpace } from "../navigation/view-scope.ts";

export interface SpaceImportCapabilities {
  actorId: string;
  request(path: string, init?: RequestInit): Promise<Response>;
  synchronize(): Promise<void>;
  defaultFilters(): ViewFilters;
  addSavedViews(views: SavedView[]): Promise<void>;
  markLocal(): void;
  progress?: ProgressReporter;
  mapped?(collections: ReadonlyMap<string, string>): void;
}

function base64Blob(value: string, type: string): Blob {
  const bytes = Uint8Array.from(atob(value), character => character.charCodeAt(0));
  return new Blob([bytes], { type });
}

export async function importPortableSpace(space: PortableSpace, targetRootId: string, views: SavedView[] = [], capabilities: SpaceImportCapabilities): Promise<void> {
  const track = <T>(stage: string, items: T[]): Iterable<[number, T]> => {
    if (items.length) capabilities.progress?.({ stage, completed: 0, total: items.length });
    return items.entries();
  };
  const done = (stage: string, index: number, total: number) => capabilities.progress?.({ stage, completed: index + 1, total });
  const categoryIds = new Map<string, string>();
  const taskIds = new Map<string, string>();
  const cycleIds = new Map<string, string>();
  const epicIds = new Map<string, string>();
  for (const epic of space.epics || []) {
    const id = randomUuid(); epicIds.set(epic.id, id);
    // Import active first so its tasks can establish membership before archiving.
    await changeEpic(id, targetRootId, capabilities.actorId, { name: epic.name, description: epic.description, ...(epic.color ? { color: epic.color } : {}), archivedAt: null });
  }
  space.tasks.forEach(source => taskIds.set(source.id, randomUuid()));
  space.categories.forEach(source => categoryIds.set(source.id, randomUuid()));
  for (const [index, source] of track("Importing collections", space.categories)) {
    const id = categoryIds.get(source.id)!;
    await createCategory(id, targetRootId, capabilities.actorId, source.name, source.viewType || "list", source.boardColumns || [], normalizeTaskSort(source.sortBy), normalizeSortDirection(source.sortDirection, source.sortBy), source.pageSize || 0, !!source.showCompleted, { customFieldIds: source.customFieldIds || [], customFieldSchema: source.customFieldSchema || {}, boardWipLimits: source.boardWipLimits || {}, boardColumnSettings: source.boardColumnSettings || {}, boardCardFields: boardCardFields(source.boardCardFields), boardAutomation: source.boardAutomation || { completedColumn: null, overdueUrgent: false, completeWithSubtasks: false }, referenceNote: remapMarkdownLinks(source.referenceNote || "", taskIds, categoryIds), referenceAnchors: source.referenceAnchors || [] });
    if (source.deleted) await changeCategory(id, targetRootId, capabilities.actorId, category => { category.deleted = true; });
    done("Importing collections", index, space.categories.length);
  }
  for (const [index, source] of track("Importing planning cycles", space.cycles || [])) {
    const id = randomUuid(); cycleIds.set(source.id, id);
    await createCycle(id, targetRootId, capabilities.actorId, { name: source.name, cadence: source.cadence, startDate: source.startDate, endDate: source.endDate, capacity: source.capacity || null, status: source.status, deleted: source.deleted });
    done("Importing planning cycles", index, space.cycles!.length);
  }
  if (space.cycles?.length || space.epics?.length) { capabilities.progress?.({ stage: "Synchronizing cycle references" }); await capabilities.synchronize(); }
  for (const [index, source] of track("Importing tasks", dependencyOrder(space.tasks))) {
    const id = taskIds.get(source.id)!;
    await changeTodo(id, targetRootId, capabilities.actorId, todo => {
      const imported = structuredClone(source);
      imported.id = id; imported.rootId = targetRootId;
      imported.description = remapMarkdownLinks(imported.description || "", taskIds, categoryIds);
      imported.categoryId = imported.categoryId ? categoryIds.get(imported.categoryId) || null : null;
      imported.parentTaskId = null;
      imported.relationships = [];
      imported.epicId = imported.epicId ? epicIds.get(imported.epicId) || null : null;
      imported.cycleId = imported.cycleId ? cycleIds.get(imported.cycleId) || null : null;
      imported.dependencyIds = []; imported.recurrenceSeriesId = imported.recurrenceSeriesId ? id : null; imported.assignedTo = [];
      imported.comments = (imported.comments || []).map(comment => ({ ...comment, id: randomUuid(), mentions: [] }));
      Object.assign(todo, imported);
    });
    done("Importing tasks", index, space.tasks.length);
  }
  if (space.epics?.some(epic => epic.archivedAt)) {
    await capabilities.synchronize();
    for (const epic of space.epics.filter(epic => epic.archivedAt)) await changeEpic(epicIds.get(epic.id)!, targetRootId, capabilities.actorId, { archivedAt: epic.archivedAt });
  }
  const linked = space.tasks.map(source => {
    const parentId = hierarchyParentId(space.tasks, source);
    const relationships = [...new Map((source.relationships || []).filter(link => link.taskId !== source.id && taskIds.has(link.taskId) && (link.type !== "parent" || link.taskId !== parentId)).map(link => {
      const normalized = link.type === "parent" ? { ...link, type: "related" as const } : link;
      return [`${normalized.type}:${normalized.taskId}`, normalized] as const;
    })).values()];
    return { source, parentId, relationships };
  });
  if (linked.some(item => item.parentId || item.source.dependencyIds?.length || item.relationships.length)) {
    capabilities.progress?.({ stage: "Preparing task relationships" });
    await capabilities.synchronize();
    const relations = linked.filter(item => item.parentId || item.source.dependencyIds?.length || item.relationships.length);
    for (const [index, { source, parentId, relationships }] of track("Importing task relationships", relations)) {
      await changeTodo(taskIds.get(source.id)!, targetRootId, capabilities.actorId, todo => {
        todo.parentTaskId = parentId ? taskIds.get(parentId) || null : null;
        todo.dependencyIds = (source.dependencyIds || []).map(dependency => taskIds.get(dependency)).filter((dependency): dependency is string => !!dependency);
        todo.relationships = relationships.map(link => ({ ...link, taskId: taskIds.get(link.taskId)! }));
      });
      done("Importing task relationships", index, relations.length);
    }
  }
  for (const [index, source] of track("Importing task templates", space.templates || [])) {
    await createTemplate(randomUuid(), targetRootId, capabilities.actorId, { ...(source.customFields ? { customFields: structuredClone(source.customFields), customFieldSchema: structuredClone(source.customFieldSchema || {}) } : {}), ...(source.purpose === "note" ? { purpose: "note" as const } : {}), name: source.name, title: source.title, description: source.description, type: source.type, subtasks: source.subtasks.map(item => ({ ...item, id: randomUuid() })), priority: source.priority, dueOffsetDays: source.dueOffsetDays, recurrence: source.recurrence ? { ...source.recurrence } : null, categoryId: source.categoryId ? categoryIds.get(source.categoryId) || null : null, tags: [...source.tags], deleted: source.deleted });
    done("Importing task templates", index, space.templates!.length);
  }
  const historyEntries = Object.entries(space.referenceHistory || {}).filter(([id]) => categoryIds.has(id) && !space.categories.find(category => category.id === id)?.deleted);
  const contentEntries = Object.entries(space.contentHistory || {}).filter(([id]) => taskIds.has(id) && !space.tasks.find(task => task.id === id)?.deleted);
  if (contentEntries.length) {
    await capabilities.synchronize();
    for (const [id, raw] of contentEntries) {
      const revisions = parseContentHistory(raw, "todo").map(revision => ({ ...revision, content: { ...revision.content, description: remapMarkdownLinks(revision.content.description, taskIds, categoryIds) } }));
      const response = await capabilities.request(`/api/v1/entities/${taskIds.get(id)}/content-history`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(revisions) });
      if (!response.ok) throw new Error("Tasks were imported, but content versions could not be restored. Keep the source backup.");
    }
  }
  if (historyEntries.length) {
    capabilities.progress?.({ stage: "Preparing Reference history restore" });
    await capabilities.synchronize();
    for (const [index, [id, raw]] of track("Restoring Reference history", historyEntries)) {
      const revisions = parseReferenceHistory(raw).map(revision => ({ ...revision, body: remapMarkdownLinks(revision.body, taskIds, categoryIds) }));
      const response = await capabilities.request(`/api/v1/collections/${categoryIds.get(id)}/reference-history`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(revisions) });
      if (!response.ok) throw new Error("Content was imported, but Reference history could not be restored. Keep the source backup and retry history import when connected.");
      done("Restoring Reference history", index, historyEntries.length);
    }
  }
  const familyIds = new Map<string, string>();
  const bundles = (space.spaceTemplates || []).filter(source => !source.deleted);
  for (const [index, source] of track("Importing space templates", bundles)) {
    if (source.deleted) continue;
    const validated = parsePortableSpaceTemplate(JSON.stringify(portableSpaceTemplate(source)));
    const familyId = familyIds.get(source.familyId) || randomUuid();
    familyIds.set(source.familyId, familyId);
    const { format: _format, ...content } = validated;
    await createSpaceTemplateBundle(randomUuid(), targetRootId, capabilities.actorId, { ...content, familyId, version: source.version, deleted: false });
    done("Importing space templates", index, bundles.length);
  }
  if (space.attachments?.length) { capabilities.progress?.({ stage: "Preparing attachment restore" }); await capabilities.synchronize(); }
  const files = (space.attachments || []).filter(item => taskIds.has(item.taskId));
  for (const [index, item] of track("Restoring attachments", files)) {
    const taskId = taskIds.get(item.taskId); if (!taskId) continue;
    const body = new FormData(); body.append("file", new File([base64Blob(item.data, item.contentType)], item.fileName, { type: item.contentType }));
    const response = await capabilities.request(`/api/v1/tasks/${taskId}/attachments`, { method: "POST", credentials: "include", body });
    if (!response.ok) throw new Error(`Could not restore attachment “${item.fileName}”.`);
    done("Restoring attachments", index, files.length);
  }
  const importedViews = views.filter(view => !view.rootId || view.rootId === space.id).map(view => {
    const sourceCategory = view.filters?.categoryId || "";
    const categoryId = sourceCategory === "none" ? "none" : categoryIds.get(sourceCategory) || "";
    const filters = { ...capabilities.defaultFilters(), ...structuredClone(view.filters), categoryId };
    filters.epicId = filters.epicId === "none" ? "none" : epicIds.get(filters.epicId || "") || "";
    if (isCrossSpace(filters)) {
      filters.spaceIds = filters.spaceIds?.map(id => id === space.id ? targetRootId : id);
      filters.collectionIds = filters.collectionIds?.map(id => categoryIds.get(id) || id);
    }
    return { ...structuredClone(view), id: randomUuid(), rootId: targetRootId, filters, published: false };
  });
  if (importedViews.length) { capabilities.progress?.({ stage: "Saving personal views" }); await capabilities.addSavedViews(importedViews); }
  capabilities.markLocal();
  capabilities.mapped?.(categoryIds);
}
